import {useState, useEffect, useRef, useMemo} from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    FormGroup,
    FormLabel,
    FormHelperText,
    CircularProgress,
    Typography,
    Stack,
    CardContent,
    Card,
    Collapse,
    Divider,
    Grid,
    TextField,
    IconButton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import { Category, ExpandLess, ExpandMore, Warning } from '@mui/icons-material';

const COD_EXTERNO_REQUIRED_MSG = 'Código obrigatório para variação ativa já registrada';

function VariationItem({ data, filho, empresa, register, setValue, getValues, errors, setError, clearErrors, actionsAllowed, selectedProduct }) {
    const statusFilho = filho?.status;
    const existeComoFilho = !!filho;
    const idFilho = filho?.id ?? null;

    const ativoInicial = getValues(`variacoes.${empresa.id}.${data.id}.ativo`);
    const [ativo, setAtivo] = useState(
        ativoInicial ?? (statusFilho === 1)
    );

    const [codExternoValue, setCodExternoValue] = useState(
        getValues(`variacoes.${empresa.id}.${data.id}.cod_externo`) ?? (filho?.cod_externo ?? '')
    );

    const alreadyExists = !!getValues(`variacoes.${empresa.id}.${data.id}.id_variacao`);
    const hasError = !!errors.variacoes?.[empresa.id]?.[data.id]?.cod_externo;

    const codExternoPath = `variacoes.${empresa.id}.${data.id}.cod_externo`;

    // Guarda o código original vindo do banco, para restaurar ao inativar.
    const originalCodExterno = useRef(getValues(codExternoPath) || '');

    // Regra: qualquer variação ativa não pode ter cod_externo vazio (cadastro ou update).
    const validarCodExterno = (value, isAtivo) => {
        if (isAtivo && !String(value ?? '').trim()) {
            setError(codExternoPath, { type: 'required', message: COD_EXTERNO_REQUIRED_MSG });
            return false;
        }
        clearErrors(codExternoPath);
        return true;
    };

    const handleCheckboxChange = (e) => {
        if (!actionsAllowed) return;

        const isChecked = e.target.checked;
        setAtivo(isChecked);

        setValue(`variacoes.${empresa.id}.${data.id}.ativo`, isChecked);
        setValue(`variacoes.${empresa.id}.${data.id}.status`, isChecked ? 1 : 0);
        setValue(`variacoes.${empresa.id}.${data.id}.is_dirty`, true);

        setValue(`variacoes.${empresa.id}.${data.id}.id_variacao`, data.id);
        setValue(`variacoes.${empresa.id}.${data.id}.id_empresa`, empresa.id);

        if (idFilho) {
            setValue(`variacoes.${empresa.id}.${data.id}.id_filho`, idFilho);
        }

        if (isChecked && !alreadyExists) {
            setValue(`variacoes.${empresa.id}.${data.id}.is_new`, true);
            setValue(`variacoes.${empresa.id}.${data.id}.rotulo`, data.tipo);
        }

        if (isChecked) {
            // Ao ativar, valida imediatamente (cadastro ou update).
            validarCodExterno(codExternoValue, true);
        }

        if (!isChecked) {
            clearErrors(codExternoPath);

            const isNew = getValues(`variacoes.${empresa.id}.${data.id}.is_new`) || false;
            if (isNew) {
                // Variação nova: limpa tudo ao desmarcar.
                setValue(`variacoes.${empresa.id}.${data.id}.cod_externo`, '');
                setCodExternoValue('');
                setValue(`variacoes.${empresa.id}.${data.id}.rotulo`, '');
                setValue(`variacoes.${empresa.id}.${data.id}.is_new`, false);
                setValue(`variacoes.${empresa.id}.${data.id}.id_variacao`, '');
                setValue(`variacoes.${empresa.id}.${data.id}.id_empresa`, '');
            } else if (alreadyExists) {
                // Variação já registrada: restaura o código original que veio do banco.
                setValue(codExternoPath, originalCodExterno.current);
                setCodExternoValue(originalCodExterno.current);
            }
        }
    };

    useEffect(() => {
        if (!data) return;
        const ativoForm = getValues(`variacoes.${empresa.id}.${data.id}.ativo`);
        setAtivo(ativoForm ?? (statusFilho === 1));
    }, [data, statusFilho]);

    // Garante o erro visual caso a variação já entre ativa e sem código.
    useEffect(() => {
        if (ativo) {
            validarCodExterno(codExternoValue, true);
        }
    }, [ativo]);

    return (
        <TableRow sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
            <TableCell padding="checkbox" sx={{ width: 50 }}>
                <Checkbox
                    size="small"
                    checked={ativo}
                    onChange={handleCheckboxChange}
                    disabled={selectedProduct ? !selectedProduct?.status : false}
                    sx={{ cursor: actionsAllowed ? 'pointer' : 'not-allowed', p: 0.5 }}
                    inputProps={{ readOnly: !actionsAllowed }}
                />
            </TableCell>
            <TableCell sx={{ py: 1.25 }}>
                <Typography variant="body2">{data.tipo.trim()}</Typography>
            </TableCell>
            <TableCell sx={{ width: 170, py: 1.25 }}>
                <TextField
                    label="Código"
                    variant="outlined"
                    value={codExternoValue}
                    {...register(codExternoPath)}
                    size="small"
                    fullWidth
                    disabled={!ativo || (selectedProduct ? !selectedProduct?.status : false)}
                    InputProps={{ readOnly: !actionsAllowed }}
                    onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setValue(codExternoPath, value);
                        setValue(`variacoes.${empresa.id}.${data.id}.is_dirty`, true);
                        setCodExternoValue(value);
                        validarCodExterno(value, ativo);
                    }}
                    error={hasError}
                    helperText={errors.variacoes?.[empresa.id]?.[data.id]?.cod_externo?.message}
                />
            </TableCell>
        </TableRow>
    );
}

function CompanyFields({ empresa, numEmpresas, variations, register, setValue, getValues, errors, setError, clearErrors, replicateVariations, selectedProduct }) {
    const [isChecked, setIsChecked] = useState(!!getValues(`id_empresas.${empresa.id}`));
    const [isOpened, setIsOpened] = useState(numEmpresas === 1);

    const variationsEmpresa = variations.filter((variation) => variation.contrato.id === empresa.idContrato);

    const contratoAtivo = variations.some(
        (v) => v.contrato.id === empresa.idContrato && v.isContractActive
    );

    const filhosPorVariacao = useMemo(() => {
        const map = new Map();
        (selectedProduct?.filhos ?? []).forEach((filho) => {
            if (filho?.variacao?.id != null) {
                map.set(filho.variacao.id, filho);
            }
        });
        return map;
    }, [selectedProduct]);

    useEffect(() => {
        setIsChecked(!!getValues(`id_empresas.${empresa.id}`));
    }, [getValues, empresa.id]);

    const renderTable = (rows) => (
        <TableContainer>
            <Table size="small">
                <TableHead sx={{ backgroundColor: 'primary.main' }}>
                    <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 42, color: 'white', py: 1, fontSize: 12 }}>Ativo</TableCell>
                        <TableCell sx={{ color: 'white', py: 0.5, fontSize: 12 }}>Variação</TableCell>
                        <TableCell sx={{ width: 170, color: 'white', py: 1.5, fontSize: 12 }}>Código</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((variacao) => (
                        <VariationItem
                            key={variacao.id}
                            data={variacao}
                            filho={filhosPorVariacao.get(variacao.id)}
                            empresa={empresa}
                            register={register}
                            setValue={setValue}
                            getValues={getValues}
                            errors={errors}
                            setError={setError}
                            clearErrors={clearErrors}
                            actionsAllowed={isChecked}
                            selectedProduct={selectedProduct}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <FormGroup>
            <Stack spacing={0.5} direction="row" alignItems="start" justifyContent="space-between" my={0.5} width={'100%'} flexWrap={'wrap'}>
                <Card
                    variant='elevation'
                    sx={{ width: '100%', backgroundColor: !isChecked && 'rgba(0, 0, 0, 0.04)', opacity: contratoAtivo ? 1 : 0.6 }}
                >
                    <CardContent
                        sx={{
                            px: 1,
                            py: isOpened ? 0.5 : 0,
                            "&:last-child": {
                                paddingBottom: isOpened ? 1.5 : 0,
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                cursor: isChecked ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                            onClick={() => isChecked && setIsOpened(prev => !prev)}
                        >
                            <IconButton size='small' disabled={!isChecked}>
                                {isOpened ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>

                            <Typography variant="body2" sx={{ ml: 1 }} color={isChecked ? 'text.primary' : 'text.disabled'}>
                                {empresa.nomeFantasia}
                            </Typography>

                            {variationsEmpresa.length === 0 ? (
                                <Stack direction="row" alignItems="center" ml={2}>
                                    <Warning color="error" fontSize="small" />
                                    <Typography color="error" variant="caption" ml={0.5}>
                                        Sem variações
                                    </Typography>
                                </Stack>
                            ) : (
                                !contratoAtivo && (
                                    <Stack direction="row" alignItems="center" ml={2}>
                                        <Warning color="error" fontSize="small" />
                                        <Typography color="error" variant="caption" ml={0.5}>
                                            Contrato inativo — este produto não pode ser configurado para esta empresa.
                                        </Typography>
                                    </Stack>
                                )
                            )}
                        </Box>

                        <Collapse in={isOpened} timeout="auto" unmountOnExit>
                            <Divider sx={{ mb: 1 }} />

                            <Box>
                                {variationsEmpresa.length > 0 && (
                                    <Card variant="outlined" sx={{ flex: 1 }}>
                                        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                                            <Stack direction="row" alignItems="center" mb={1}>
                                                <Category sx={{ mr: 1 }} fontSize="small" color={'info'} />
                                                <Typography sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} mb={2}>
                                                    Variações disponíveis para a empresa:
                                                </Typography>
                                            </Stack>

                                            <Grid container spacing={1}>
                                                {variationsEmpresa.length === 1 ? (
                                                    <Grid item xs={12}>
                                                        {renderTable(variationsEmpresa)}
                                                    </Grid>
                                                ) : (
                                                    [0, 1].map((columnIndex) => {
                                                        const columnVariations = variationsEmpresa.filter((_, index) => index % 2 === columnIndex);
                                                        return (
                                                            <Grid item xs={12} md={6} key={columnIndex}>
                                                                {renderTable(columnVariations)}
                                                            </Grid>
                                                        );
                                                    })
                                                )}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                )}
                            </Box>
                        </Collapse>
                    </CardContent>
                </Card>
            </Stack>
        </FormGroup>
    );
}

export function CompanyVariations({ empresasAssociadas, variations, register, setValue, getValues, errors, setError, clearErrors, selectedProduct }) {
    const replicateVariations = (vidaUtil) => {
        empresasAssociadas.forEach((empresa) => {
            const isChecked = getValues(`id_empresas.${empresa.id}`);
            if (isChecked) {
                setValue(`vida_util.${empresa.id}`, vidaUtil);
            }
        });
    };

    const numEmpresas = empresasAssociadas ? empresasAssociadas.length : 0;

    return (
        <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
            <FormControl sx={{ mx: 1 }} component="fieldset" variant="standard">
                <FormLabel component="legend" sx={{ mb: 0.5 }}>
                    Selecione as variações para o produto em cada empresa:
                </FormLabel>
                <>
                    {empresasAssociadas ? empresasAssociadas.map((empresa) => (
                            <FormControl key={empresa.id}>
                                <CompanyFields
                                    empresa={empresa}
                                    numEmpresas={numEmpresas}
                                    variations={variations}
                                    register={register}
                                    setValue={setValue}
                                    getValues={getValues}
                                    errors={errors}
                                    setError={setError}
                                    clearErrors={clearErrors}
                                    replicateVariations={replicateVariations}
                                    selectedProduct={selectedProduct}
                                />
                            </FormControl>
                        ))
                        : <Typography variant='body2' sx={{ mt: 2 }}><CircularProgress size={20} /> Carregando empresas...</Typography>
                    }
                </>
                <FormHelperText>Selecione ao menos uma Variação</FormHelperText>
            </FormControl>
        </Box>
    );
}